import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import Header from "../../../commons/Header/Header";
import Sidebar from "../../../commons/Sidebar/Sidebar";
import viewImage from "../../../assets/images/icons/view.svg";
import Img from "../../../assets/images/img.webp";
import { Link } from "react-router-dom";
import TablePagination from "../../../commons/Pagination/TablePagination";
import { handleApiCall } from "../../../apis";
import { endpoints } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../../utils/constants";

const DESCRIPTION_WORD_LIMIT = 50;

const getDishImageUrl = (image) => {
  if (!image || typeof image !== "string") return Img;
  if (image.startsWith("http")) return image;
  const base = process.env.REACT_APP_IMAGE_URL || "";
  if (!base) return Img;
  return `${base.replace(/\/$/, "")}/${image.replace(/^\//, "")}`;
};

const truncateWords = (text, wordLimit = DESCRIPTION_WORD_LIMIT) => {
  if (!text) return "N/A";
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  if (words.length <= wordLimit) return words.join(" ");
  return `${words.slice(0, wordLimit).join(" ")}...`;
};

const formatDishRating = (value) => {
  if (value === undefined || value === null || value === "") return "N/A";
  const rating = Number(value);
  if (Number.isNaN(rating)) return String(value);
  return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
};

function ViewMenuCatalogue() {
  const { id, name } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [restaurantName, setRestaurantName] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const getMenuCatalogue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        limit: limit,
        ...(search && { search: search }),
      });

      const response = await handleApiCall("get", `${endpoints.restaurantMenuCatalogue}/${id}?${params}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setMenuItems(response?.data?.data?.data || []);
        setTotalPages(response?.data?.data?.pagination?.totalPages || 1);
      } else {
        toast.error(response?.data?.message || "Failed to fetch menu catalogue");
      }
    } catch (error) {
      console.error("Error fetching menu catalogue:", error);
      toast.error("Failed to fetch menu catalogue");
    } finally {
      setLoading(false);
    }
  }, [id, page, limit, search]);

  useEffect(() => {
    const listState = location.state?.listState;
    if (!listState) return;
    if (listState.page) setPage(listState.page);
    if (listState.search !== undefined) setSearch(listState.search);
  }, [location.state]);

  useEffect(() => {
    if (id) {
      setRestaurantName(decodeURIComponent(name || "Restaurant"));
      getMenuCatalogue();
    }
  }, [id, name, getMenuCatalogue]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Menu Catalogue Of {restaurantName || "Restaurant"}</h3>
            </div>
            <div className="mt-3 mb-3">
              <Link to="/menu-and-restaurant-management" className="btn btn-default rounded-lg">
                Back
              </Link>
            </div>

            {/* Search Bar */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="form-group my-0">
                  <input
                    type="text"
                    className="form-control search-icon left-icon border"
                    placeholder={UI_CONSTANTS.PLACEHOLDERS.MENU_CATALOGUE_SEARCH}
                    value={search}
                    onChange={handleSearch}
                  />
                </div>
              </div>
            </div>
            <div className="mb-4 px-0">
              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim menu-catalogue-table">
                    <colgroup>
                      <col className="menu-catalogue-table__col-sno" />
                      <col className="menu-catalogue-table__col-dish" />
                      <col className="menu-catalogue-table__col-description" />
                      <col className="menu-catalogue-table__col-size" />
                      <col className="menu-catalogue-table__col-rating" />
                      <col className="menu-catalogue-table__col-status" />
                      <col className="menu-catalogue-table__col-action" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="menu-catalogue-table__sno-col">S.no.</th>
                        <th className="menu-catalogue-table__dish-col">Dish name</th>
                        <th className="menu-catalogue-table__description-col">Description</th>
                        <th className="menu-catalogue-table__size-col">Size</th>
                        <th className="menu-catalogue-table__rating-col">Dish rating</th>
                        <th className="menu-catalogue-table__status-col">Status</th>
                        <th className="menu-catalogue-table__action-col text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : menuItems.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      ) : (
                        menuItems.map((item, index) => (
                          <tr key={item._id}>
                            <td className="menu-catalogue-table__sno-cell">
                              {(page - 1) * limit + index + 1}
                            </td>
                            <td className="menu-catalogue-table__dish-cell">
                              <div className="menu-catalogue-table__dish">
                                <img
                                  src={getDishImageUrl(item.image)}
                                  alt={item.dish_name || "Dish"}
                                  className="menu-catalogue-table__dish-avatar"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = Img;
                                  }}
                                />
                                <span className="menu-catalogue-table__dish-name">
                                  {item.dish_name || "N/A"}
                                </span>
                              </div>
                            </td>
                            <td
                              className="menu-catalogue-table__description-cell text-center"
                              title={item.description || undefined}
                            >
                              <span className="menu-catalogue-table__description-text text-center">
                                {truncateWords(item.description)}
                              </span>
                            </td>
                            <td className="menu-catalogue-table__size-cell">
                              {item.dish_price_quantity && item.dish_price_quantity.length > 0
                                ? item.dish_price_quantity.map((priceItem, priceIndex) => priceItem.variation_detail?.name || "N/A").join(" / ")
                                : "N/A"}
                            </td>
                            <td className="menu-catalogue-table__rating-cell">
                              {formatDishRating(item.dish_average_rating)}
                            </td>
                            <td className="menu-catalogue-table__status-cell">
                              <span className={`badge ${item.status === 1 ? "badge-success" : "badge-danger"}`}>{item.status === 1 ? "Active" : "Inactive"}</span>
                            </td>
                            <td className="menu-catalogue-table__action-cell">
                              <div className="d-flex align-items-center justify-content-center">
                                <Link
                                  to={`/menu-and-restaurant/view-menu-catalogue/item-details/${item._id}`}
                                  state={{
                                    from: `/menu-and-restaurant/view-menu-catalogue/${id}/${encodeURIComponent(name || restaurantName)}`,
                                    listState: { page, search },
                                  }}
                                  className="mx-2"
                                >
                                  <img src={viewImage} alt="View" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {totalPages > 1 && (
                <TablePagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewMenuCatalogue;
